import { Copy, KeyRound, ShieldAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { BACKEND_URL } from "@/lib/api";

const slotStyles = {
  ready: "border-emerald-200 bg-emerald-50 text-emerald-700",
  pending: "border-amber-200 bg-amber-50 text-amber-700",
};

export const MissionControlEnvironmentPanel = ({ defaultMode = "sandbox", onModeChange, productionSlots = [], twilioWebhookPath = "" }) => {
  const [mode, setMode] = useState(defaultMode);
  const [copied, setCopied] = useState(false);
  const webhookUrl = useMemo(() => `${BACKEND_URL}${twilioWebhookPath || ""}`, [twilioWebhookPath]);

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className="rounded-[32px] border border-[#001935]/10 bg-white/95 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.08)] md:p-8" data-testid="mission-control-environment-panel">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]">Environment Mode</p>
          <h3 className="mt-2 text-2xl font-semibold text-[#001935]">Sandbox and production cutover slots</h3>
          <p className="mt-3 max-w-3xl text-sm leading-7 text-[#42566f]">
            Toggle the Mission Control view between the current sandbox posture and the production cutover checklist. Switching to PRODUCTION exposes the live Stripe, messaging, and Resend credential slots required before go-live.
          </p>
        </div>

        <div className="inline-flex rounded-full border border-[#001935]/10 bg-[#f8f5ef] p-1" data-testid="mission-control-environment-toggle">
          {[
            ["sandbox", "SANDBOX"],
            ["production", "PRODUCTION"],
          ].map(([value, label]) => (
            <button
              className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] transition ${mode === value ? "bg-[#001935] text-white" : "text-[#42566f] hover:bg-white"}`}
              data-testid={`mission-control-environment-mode-${value}`}
              key={value}
              onClick={() => {
                setMode(value);
                onModeChange?.(value);
              }}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 rounded-[24px] border border-[#D4AF37]/25 bg-[#fffaf1] p-5" data-testid="mission-control-twilio-webhook-card">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">WhatsApp Webhook URL</p>
            <p className="mt-2 text-sm leading-7 text-[#42566f]">
              Paste this inbound webhook URL into your live messaging console when you are ready to move marketplace enquiries from the current mocked alert path to the live WhatsApp lane.
            </p>
          </div>
          <button className="outline-button !px-4 !py-2" data-testid="mission-control-copy-twilio-webhook-button" onClick={copyWebhookUrl} type="button">
            <Copy className="mr-2 h-4 w-4" /> {copied ? "Copied" : "Copy URL"}
          </button>
        </div>
        <div className="mt-4 rounded-[20px] border border-[#001935]/10 bg-white px-4 py-4 text-sm font-semibold text-[#001935]" data-testid="mission-control-twilio-webhook-url">
          {webhookUrl}
        </div>
      </div>

      {mode === "production" ? (
        <>
          <div className="mt-6 rounded-[24px] border border-amber-200 bg-amber-50 p-5" data-testid="mission-control-production-prompt">
            <div className="flex items-start gap-3">
              <ShieldAlert className="mt-0.5 h-5 w-5 text-amber-700" />
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-700">Production Credential Prompt</p>
                <p className="mt-2 text-sm leading-7 text-amber-900">
                  Before switching live traffic to PRODUCTION, paste your live <strong>Stripe Secret Key</strong>, <strong>Stripe Webhook Secret</strong>, and <strong>messaging credentials</strong>. The slots below are intentionally blank until you approve the real cutover.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="mission-control-production-slots-grid">
            {productionSlots.map((slot) => (
              <div className="rounded-[24px] border border-[#001935]/10 bg-white p-5" data-testid={`mission-control-production-slot-${slot.key}`} key={slot.key}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-[#fff4db] p-3 text-[#8b6d16]">
                      <KeyRound className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">{slot.service}</p>
                      <p className="mt-1 text-base font-semibold text-[#001935]">{slot.label}</p>
                    </div>
                  </div>
                  <span className={`rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] ${slotStyles[slot.status] || slotStyles.pending}`} data-testid={`mission-control-production-slot-status-${slot.key}`}>
                    {slot.status}
                  </span>
                </div>
                <p className="mt-4 text-sm leading-7 text-[#42566f]">{slot.prompt}</p>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="mt-6 rounded-[24px] border border-[#001935]/10 bg-[#f8f5ef] p-5" data-testid="mission-control-sandbox-mode-note">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Sandbox Active</p>
          <p className="mt-2 text-sm leading-7 text-[#42566f]">
            Mission Control is currently showing the sandbox posture. Founder checkout stays on the test Stripe path, messaging remains in mocked-prep mode, and production slots stay hidden until you intentionally switch this panel to PRODUCTION.
          </p>
        </div>
      )}
    </section>
  );
};